<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useDebounceFn, useResizeObserver } from '@vueuse/core';
import { VuePDF, usePDF } from '@tato30/vue-pdf';
import type { TextItem, TextMarkedContent } from 'pdfjs-dist/types/src/display/api';

import {
  getVaultFileByRevision,
  getVaultFileMetadata,
  type VaultFileInDbOwner,
  type VaultFileInDbSlim,
} from '@/api';
import { useAuthStore } from '@/stores/auth';
import { useVaultWebSocket } from '@/composables/useVaultWebSocket';
import { logger } from '@/utils/logger';
import { pdfjsAssetOptions } from '@/utils/pdfjsAssets';
import GaiaButton from '../../base/GaiaButton.vue';
import GaiaIconButton from '../../base/GaiaIconButton.vue';
import GaiaInput from '../../base/inputs/GaiaInput.vue';
import GaiaSpinner from '../../base/GaiaSpinner.vue';
import GaiaText from '../../base/GaiaText.vue';
import GaiaDocPager from '../../composite/GaiaDocPager.vue';
import GaiaEmptyState from '../../composite/GaiaEmptyState.vue';
import { gaiaDiff, gaiaNeutral, gaiaShadow } from '../../themes/tokens';

const authStore = useAuthStore();
const token = ref<string | undefined>(undefined);
// Either uid works alone: `vaultFileUid` previews the file's current revision,
// `revisionUid` previews exactly that revision (the parent file is resolved
// when needed). Passing both pins the revision and skips the resolution.
const props = withDefaults(
  defineProps<{
    vaultFileUid?: string;
    downloadUrl?: string;
    revisionUid?: string;
    revisionPreviewStatus?: string | null;
    highlightColor?: 'red' | 'green';
    /**
     * `continuous` (the default) renders the document as one scrollable column
     * — you read across a page break without asking, which is how a document is
     * meant to behave. Only the pages near the viewport are rasterised.
     *
     * `paginated` renders one page at a time. A deliberate choice, for a pane
     * too narrow or too short for scrolling to read well.
     *
     * Both modes share everything else — loading, preview status, highlight
     * matching, download — and both drive the same `currentPage`, so the
     * exposed API and the `pageChange` emit behave identically either way.
     */
    mode?: 'paginated' | 'continuous';
    /**
     * Zoom buttons in the pager (continuous mode only). Off by default — the
     * signing wizard's field placement opts in.
     */
    zoomable?: boolean;
    /**
     * The zoom a document opens at, and the one the zoom readout resets to
     * (continuous mode) — below 1 for a wide pane, where a page drawn at full
     * width is mostly offscreen.
     */
    defaultZoom?: number;
  }>(),
  {
    vaultFileUid: undefined,
    downloadUrl: undefined,
    revisionUid: undefined,
    revisionPreviewStatus: undefined,
    highlightColor: undefined,
    mode: 'continuous',
    zoomable: false,
    defaultZoom: 1,
  }
);

const emit = defineEmits<{
  pageChange: [page: number];
  loaded: [totalPages: number];
  highlightsCleared: [];
}>();

const viewerBorder = gaiaNeutral.borderMuted;
const viewerCanvas = gaiaNeutral.surfaceTint;
const pageSurface = gaiaNeutral.surface;
const pageLift = gaiaShadow.card;

const pdfInfo = ref();
const loaded = ref(false);
const error = ref<string>('');
const isInitializing = ref(false);
const currentPage = ref(1);
const totalPages = ref(0);
const jumpToPage = ref('');
const downloadingFile = ref(false);
const vuePDFRef = ref<InstanceType<typeof VuePDF> | null>(null);
const pdfContainerRef = ref<HTMLElement | null>(null);
let lastContainerWidth = 0;
const isContinuous = computed(() => props.mode === 'continuous');
/**
 * Page wrappers in continuous mode, by page number, for scrolling and observing.
 * Deliberately not reactive: it is written from a `:ref` callback during render,
 * and nothing in the template reads it back.
 */
const pageEls = new Map<number, HTMLElement>();
/**
 * How many pages either side of the current one are actually rasterised. Each
 * canvas is roughly width x height x 4 bytes — about 5 MB on a wide pane — so a
 * long contract with every page mounted would cost more memory than the tab
 * has. Everything outside the window keeps a correctly-sized spacer instead, so
 * the scrollbar still describes the whole document.
 */
const RENDER_WINDOW = 2;
/** Page number -> aspect ratio (height / width), for sizing those spacers. */
const pageRatios = ref<Map<number, number>>(new Map());
/** Page number -> width in PDF points, for overlays that size things in pt. */
const pageWidthsPts = ref<Map<number, number>>(new Map());
/** Set while goToPage is scrolling, so the observer doesn't fight the scroll. */
let scrollingToPage = false;
/** Bumped to re-key the mounted pages when the container width changes. */
const renderNonce = ref(0);
/**
 * Width of one page box. Read once per resize rather than per page per render:
 * every spacer needs it, and clientWidth forces a layout.
 */
const pageBoxWidth = ref(0);

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2;
const ZOOM_STEP = 0.25;
/** Page width as a multiple of the pane's width. */
const zoom = ref(1);
const showZoom = computed(() => props.zoomable && isContinuous.value);

// Vault file metadata (slim when resolved from a revision uid)
const vaultFileData = ref<VaultFileInDbOwner | VaultFileInDbSlim | null>(null);
const isCheckingPreview = ref(false);

const highlightOptions = ref({
  completeWords: false,
  ignoreCase: true,
});

// Reactive property to store extracted page text
const pageText = ref<string>('');

// Reactive highlight text that can be set externally
const currentHighlightText = ref<string[]>([]);
const highlightMode = ref<'smart' | 'direct'>('smart');

// Computed properties
const isValidPageNumber = computed(() => {
  const page = Number(jumpToPage.value);
  return !isNaN(page) && page >= 1 && page <= totalPages.value;
});

const fileName = computed(() => {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
  return vaultFileData.value?.current_revision?.file_name || 'Document';
});

const effectivePreviewStatus = computed(() => {
  if (props.revisionUid && props.revisionPreviewStatus !== undefined) {
    return props.revisionPreviewStatus;
  }
  const currentRevision = vaultFileData.value?.current_revision;
  if (!props.revisionUid || currentRevision?.uid === props.revisionUid) {
    return currentRevision?.preview_status ?? null;
  }
  // An older revision was preprocessed back when it was the current one;
  // if its preview is gone after all, loading the PDF surfaces the error.
  return currentRevision ? 'done' : null;
});

const isPreviewReady = computed(() => effectivePreviewStatus.value === 'done');
const isPreviewInProgress = computed(() => effectivePreviewStatus.value === 'running');
const isPreviewError = computed(() => effectivePreviewStatus.value === 'failed');

const hasHighlights = computed(() => {
  return currentHighlightText.value.length > 0;
});

async function loadToken(): Promise<boolean> {
  try {
    const authToken = await authStore.getAccessToken();
    if (authToken) {
      token.value = authToken;
      return true;
    }
    return false;
  } catch (error_) {
    logger.error('Failed to load token:', error_);
    error.value = `Failed to load token: ${error_ instanceof Error ? error_.message : 'Unknown error'}`;
    return false;
  }
}

async function fetchVaultFileData(): Promise<void> {
  if (!token.value || (!props.vaultFileUid && !props.revisionUid)) {
    return;
  }
  // Caller-supplied preview status makes the lookup unnecessary: the revision
  // uid builds the URL and the status determines readiness.
  if (props.revisionUid && props.revisionPreviewStatus !== undefined) {
    return;
  }

  try {
    isCheckingPreview.value = true;
    const response = props.vaultFileUid
      ? await getVaultFileMetadata({ path: { file_uid: props.vaultFileUid } })
      : props.revisionUid
        ? await getVaultFileByRevision({ path: { revision_uid: props.revisionUid } })
        : undefined;

    if (!response?.data) {
      throw new Error('Failed to fetch vault file data');
    }
    vaultFileData.value = response.data;
  } catch (error_) {
    logger.error('Failed to fetch vault file data:', error_);
    error.value = `Failed to fetch file metadata: ${error_ instanceof Error ? error_.message : 'Unknown error'}`;
  } finally {
    isCheckingPreview.value = false;
  }
}

function resetPreviewState(): void {
  pdfInfo.value = undefined;
  zoom.value = 1;
  vaultFileData.value = null;
  loaded.value = false;
  error.value = '';
  isInitializing.value = false;
  currentPage.value = 1;
  totalPages.value = 0;
  jumpToPage.value = '';
  pageText.value = '';
}

const pdfUrl = computed(() => {
  const revisionUid = props.revisionUid ?? vaultFileData.value?.current_revision?.uid;
  if (!revisionUid) return '';
  return `${import.meta.env.VITE_API_URL}/v1/app/repository/vault/file/revision/${revisionUid}/preview`;
});

function initializePDF(): void {
  if (!token.value || !pdfUrl.value || !isPreviewReady.value) {
    return;
  }

  if (isInitializing.value) {
    return;
  }

  try {
    isInitializing.value = true;
    error.value = '';

    // Clean up previous PDF if it exists
    if (pdfInfo.value) {
      pdfInfo.value = undefined;
      loaded.value = false;
    }

    // Initialize PDF
    const pdf = usePDF({
      url: pdfUrl.value,
      httpHeaders: {
        Authorization: token.value.startsWith('Bearer ') ? token.value : `Bearer ${token.value}`,
      },
      rangeChunkSize: 1024 * 1024, // 1 mb at a time
      disableStream: true,
      ...pdfjsAssetOptions,
    });

    // Set up watchers for PDF state. The page count lands before `loaded`
    // flips: measuring the pages and starting the scroll observer both run off
    // `loaded`, and a count of zero at that moment would leave every page
    // unmeasured — spacers at a guessed height and the reading position wrong.
    watch(pdf.pdf, async newPdf => {
      if (newPdf) {
        const pdfDoc = await newPdf.promise;
        totalPages.value = pdfDoc.numPages;
        loaded.value = true;
        emit('loaded', totalPages.value);
      }
    });

    pdfInfo.value = pdf;
  } catch (error_) {
    logger.error('Failed to initialize PDF:', error_);
    error.value = `Failed to initialize PDF: ${error_ instanceof Error ? error_.message : 'Unknown error'}`;
    loaded.value = false;
  } finally {
    isInitializing.value = false;
  }
}

async function syncPreviewToCurrentFile(): Promise<void> {
  resetPreviewState();

  if (!token.value || (!props.vaultFileUid && !props.revisionUid)) {
    return;
  }

  await fetchVaultFileData();

  // When the preview isn't ready yet we simply wait: the backend emits
  // `vault_file_updated` once preprocessing finishes, which `useVaultWebSocket`
  // delivers below to load the PDF (no polling).
  if (isPreviewReady.value) {
    initializePDF();
  }
}

onMounted(async () => {
  await loadToken();
});

// Watch for changes in token or preview target
watch(
  [
    token,
    (): string | undefined => props.vaultFileUid,
    (): string | undefined => props.revisionUid,
  ],
  () => {
    void syncPreviewToCurrentFile();
  },
  { immediate: true }
);

// Live preview-status updates: subscribe to the vault file and load the PDF
// when preprocessing completes. With a caller-supplied status there is nothing
// to watch; in revision mode the parent file resolved from the revision is
// watched instead.
useVaultWebSocket({
  fileUids: () => {
    if (props.revisionUid && props.revisionPreviewStatus !== undefined) return [];
    const fileUid = props.vaultFileUid ?? vaultFileData.value?.uid;
    return fileUid ? [fileUid] : [];
  },
  onFileUpdated: (_uid, fileData) => {
    vaultFileData.value = fileData as unknown as VaultFileInDbOwner;
    if (isPreviewReady.value && !loaded.value) {
      initializePDF();
    }
  },
});

async function applyHighlights(): Promise<void> {
  if (currentHighlightText.value.length === 0) return;
  if (highlightMode.value === 'direct') {
    highlightTextForPDF.value = currentHighlightText.value;
  } else {
    await setHighlightText(currentHighlightText.value);
  }
}

// Watch for changes in highlight text
watch(currentHighlightText, async newTexts => {
  if (newTexts.length > 0) {
    await applyHighlights();
  }
});

// Reapply highlighting when the page changes (smart mode only; in direct mode
// the parent manages per-page highlights).
//
// In continuous mode this only runs for a deliberate jump, not for scrolling:
// re-deriving means extracting the page's text and running the whole
// exact/fuzzy/word cascade over it, and doing that for every page the reader
// scrolls past would thrash for no benefit — the parent set those highlights
// for one specific page.
watch(currentPage, async () => {
  if (highlightMode.value === 'direct') return;
  if (isContinuous.value && !scrollingToPage) return;
  await applyHighlights();
});

// Reapply when PDF finishes loading (handles the case where highlights were set before PDF was ready)
watch(loaded, async isLoaded => {
  if (isLoaded) await applyHighlights();
});

// Page navigation. Every entry point funnels through goToPage so the scroll
// behaviour in continuous mode only has to exist in one place.
//
// `at` (continuous mode) is a point on the page as percentages of its box —
// a signature field's x/y — brought a third of the way into the viewport
// rather than scrolling to the page's top edge.
function goToPage(page: number, at?: { x: number; y: number }): void {
  if (page < 1 || page > totalPages.value) return;

  currentPage.value = page;
  emit('pageChange', page);

  if (!isContinuous.value) return;

  // Let the render window widen around the new page before scrolling to it —
  // its wrapper may currently be a spacer of the right height but no content.
  scrollingToPage = true;
  void nextTick(() => {
    const pageEl = pageEls.get(page);
    const root = pdfContainerRef.value;
    if (at && pageEl && root) {
      // Spacers hold the page's true size, so the point is right even before
      // the page is rasterised.
      const pageRect = pageEl.getBoundingClientRect();
      const rootRect = root.getBoundingClientRect();
      root.scrollTop +=
        pageRect.top - rootRect.top + (pageRect.height * at.y) / 100 - root.clientHeight / 3;
      root.scrollLeft +=
        pageRect.left - rootRect.left + (pageRect.width * at.x) / 100 - root.clientWidth / 3;
    } else {
      pageEl?.scrollIntoView({ block: 'start' });
    }
    // The observer fires through the pages we passed on the way; ignore those
    // and let it resume once the scroll has settled.
    setTimeout(() => {
      scrollingToPage = false;
    }, 300);
  });
}

function goToNextPage(): void {
  goToPage(currentPage.value + 1);
}

function goToPreviousPage(): void {
  goToPage(currentPage.value - 1);
}

function goToFirstPage(): void {
  goToPage(1);
}

function goToLastPage(): void {
  goToPage(totalPages.value);
}

/**
 * In continuous mode the page number follows the scroll rather than driving it.
 * The most-visible page wins, so a page break doesn't flicker the readout.
 */
let pageObserver: IntersectionObserver | null = null;
/**
 * Pages currently intersecting the viewport. Kept as a running set rather than
 * read off each callback's `entries`, because a callback only carries the pages
 * that *crossed a threshold* in that tick — usually just the one leaving and
 * the one arriving. Choosing from that partial set is what reported the page
 * you had already scrolled past.
 */
const visiblePages = new Set<number>();
let pickQueued = false;

function observePage(page: number, el: HTMLElement | null): void {
  if (!el) {
    pageEls.delete(page);
    visiblePages.delete(page);
    return;
  }
  pageEls.set(page, el);
  el.dataset.page = String(page);
  pageObserver?.observe(el);
}

/**
 * The page under the reading line — a quarter of the way down the viewport,
 * the way a PDF reader names the page you are actually looking at.
 *
 * Geometry, not `intersectionRatio`: that ratio is relative to the page's own
 * height, so a page taller than the viewport can never score high and two tall
 * neighbours sit at similar low values and flip-flop between them.
 */
function pickCurrentPage(): void {
  const root = pdfContainerRef.value;
  if (scrollingToPage || !isContinuous.value || !root || visiblePages.size === 0) return;

  const rootRect = root.getBoundingClientRect();
  const line = rootRect.top + rootRect.height * 0.25;

  const pages = [...visiblePages].sort((a, b) => a - b);
  let chosen: number | undefined;
  for (const page of pages) {
    const rect = pageEls.get(page)?.getBoundingClientRect();
    if (rect && rect.top <= line && rect.bottom > line) {
      chosen = page;
      break;
    }
  }
  // Above the first page, or the line happens to fall in the gap between two:
  // the topmost visible page is the one being read.
  chosen ??= pages[0];

  if (chosen !== currentPage.value) {
    currentPage.value = chosen;
    emit('pageChange', chosen);
  }
}

/** Coalesce to one measurement per frame — scroll fires far faster than layout. */
function queuePick(): void {
  if (pickQueued) return;
  pickQueued = true;
  requestAnimationFrame(() => {
    pickQueued = false;
    pickCurrentPage();
  });
}

function startPageObserver(): void {
  stopPageObserver();
  const root = pdfContainerRef.value;
  if (!isContinuous.value || !root) return;

  // The observer only maintains the visible set, so the geometry loop above
  // stays over a handful of pages instead of the whole document.
  pageObserver = new IntersectionObserver(
    entries => {
      for (const entry of entries) {
        const page = Number((entry.target as HTMLElement).dataset.page);
        if (!page) continue;
        if (entry.isIntersecting) {
          visiblePages.add(page);
        } else {
          visiblePages.delete(page);
        }
      }
      queuePick();
    },
    { root, threshold: 0 }
  );

  for (const el of pageEls.values()) pageObserver.observe(el);

  // Threshold crossings alone are too coarse: scrolling within one tall page
  // produces no entries at all, so the readout would stick.
  root.addEventListener('scroll', queuePick, { passive: true });
  measurePageBox();
  queuePick();
}

/** All page boxes are the same width, so one measurement serves every spacer. */
function measurePageBox(): void {
  const first = pageEls.values().next().value;
  if (first) pageBoxWidth.value = first.clientWidth;
}

function stopPageObserver(): void {
  pageObserver?.disconnect();
  pageObserver = null;
  visiblePages.clear();
  pdfContainerRef.value?.removeEventListener('scroll', queuePick);
}

/**
 * Measure every page once so unrendered pages can hold their true height. This
 * only reads page geometry — no rasterising — so it is cheap even on a long
 * document, and it is what keeps the scrollbar honest.
 */
async function measurePages(): Promise<void> {
  if (!isContinuous.value || !pdfInfo.value?.pdf) return;

  try {
    const pdf = await pdfInfo.value.pdf.promise;
    const ratios = new Map<number, number>();
    const widths = new Map<number, number>();
    for (let page = 1; page <= totalPages.value; page++) {
      const pdfPage = await pdf.getPage(page);
      // Scale 1 is PDF points, rotation applied.
      const viewport = pdfPage.getViewport({ scale: 1 });
      ratios.set(page, viewport.height / viewport.width);
      widths.set(page, viewport.width);
    }
    pageRatios.value = ratios;
    pageWidthsPts.value = widths;
  } catch (error_) {
    logger.error('[PdfPreview] Could not measure pages:', error_);
  }
}

// Measure and start observing once the document is open; tear the observer
// down with the component so it cannot outlive the element it watches.
watch(loaded, async isLoaded => {
  if (!isLoaded) {
    stopPageObserver();
    return;
  }
  await measurePages();
  await nextTick();
  startPageObserver();
  if (isContinuous.value) await setZoom(props.defaultZoom);
});

onBeforeUnmount(stopPageObserver);

/** The pages actually rasterised right now — the rest are spacers. */
function isRendered(page: number): boolean {
  return Math.abs(page - currentPage.value) <= RENDER_WINDOW;
}

/**
 * The box every page keeps, rendered or not, from its measured aspect ratio.
 *
 * Sized off the page box, not the scroll container: the column is inset by the
 * viewer's padding, so using the container width made every spacer taller than
 * the page it stands in for. Content above the viewport then changed height as
 * pages left the render window, shifting the scroll under the reader — which
 * looks exactly like the document jumping back a page.
 *
 * A rendered page reserves the same height as a minimum. Its canvas paints
 * asynchronously, and until it does the page would be zero pixels tall — the
 * spacers below would slide up under the reading line and the observer would
 * pick a page further down than the one on screen. The canvas ends up at
 * exactly this height (width x ratio), so the minimum never distorts it.
 *
 * All of this assumes the container is the element that scrolls. A host that
 * lets the container grow with its content (a drawer body under naive's
 * styled scrollbar) makes the reading line a quarter of the way down the whole
 * document — such hosts use `gaia-drawer flush`, which hands the body's height
 * to the preview.
 */
function pageBoxStyle(page: number): { height: string } | { minHeight: string } | undefined {
  const ratio = pageRatios.value.get(page);
  const measured =
    ratio && pageBoxWidth.value ? `${Math.round(pageBoxWidth.value * ratio)}px` : null;
  if (!isRendered(page)) return { height: measured ?? '80vh' };
  return measured ? { minHeight: measured } : undefined;
}

function handleJumpToPage(): void {
  if (isValidPageNumber.value) {
    const page = Number(jumpToPage.value);
    goToPage(page);
    jumpToPage.value = '';
  }
}

// Download functionality
function downloadFile(): void {
  if (!props.downloadUrl) {
    logger.error('No download URL provided');
    return;
  }

  try {
    downloadingFile.value = true;

    const link = document.createElement('a');
    link.href = props.downloadUrl;
    link.download = fileName.value;
    link.click();
  } catch (error) {
    logger.error('Error downloading file:', error);
  } finally {
    downloadingFile.value = false;
  }
}

// Update pageInput when currentPage changes
watch(currentPage, newPage => {
  jumpToPage.value = newPage.toString();
});

// Function to reload PDF when container size changes
function reloadPDF(): void {
  if (isContinuous.value) {
    // There is no single VuePDF instance to reload here — bumping the nonce
    // re-keys the mounted pages, which is what makes fit-parent re-measure.
    renderNonce.value++;
    return;
  }
  if (vuePDFRef.value) {
    vuePDFRef.value.reload();
  }
}

/**
 * Pages are drawn at `zoom` x the pane's width — fit-parent re-renders at the
 * new width, so the text stays sharp rather than being scaled. Every page grows
 * by the same factor, so keeping the scroll ratios keeps the reader's place.
 */
async function setZoom(next: number): Promise<void> {
  const value = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next));
  if (value === zoom.value) return;

  const root = pdfContainerRef.value;
  const topRatio = root ? root.scrollTop / root.scrollHeight : 0;
  const leftRatio = root ? root.scrollLeft / root.scrollWidth : 0;

  zoom.value = value;
  await nextTick();
  measurePageBox();
  reloadPDF();
  await nextTick();

  if (root) {
    root.scrollTop = topRatio * root.scrollHeight;
    root.scrollLeft = leftRatio * root.scrollWidth;
  }
}

// VuePDF's fit-parent only measures on render, so re-render when the container
// width changes: split-pane resizes, or being re-shown at a different size after
// the pane was hidden/moved. Zero width means hidden (display: none) — skip and
// wait for the real size.
const reloadOnResize = useDebounceFn(reloadPDF, 200);
useResizeObserver(pdfContainerRef, entries => {
  const width = entries[0]?.contentRect.width ?? 0;
  if (width === 0 || width === lastContainerWidth) return;
  lastContainerWidth = width;
  measurePageBox();
  void reloadOnResize();
});

// Function to extract text from the current PDF page
async function extractPageText(): Promise<string> {
  if (!pdfInfo.value?.pdf || !currentPage.value) {
    return '';
  }

  try {
    const pdf = await pdfInfo.value.pdf.promise;
    const page = await pdf.getPage(currentPage.value);
    const textContent = await page.getTextContent();

    // Extract text from text content
    const text = textContent.items
      .map((item: TextItem | TextMarkedContent) => ('str' in item ? item.str : ''))
      .join(' ');

    return text;
  } catch {
    return '';
  }
}

// Function to find exact matches in text
function findExactMatches(text: string, searchStrings: string[]): string[] {
  const matches: string[] = [];

  for (const searchString of searchStrings) {
    // Normalize spaces in both search string and text
    const normalizedSearch = searchString.replace(/\s+/g, ' ').trim();
    const normalizedText = text.replace(/\s+/g, ' ').trim();

    const searchLower = normalizedSearch.toLowerCase();
    const textLower = normalizedText.toLowerCase();

    const isMatch = textLower.includes(searchLower);

    if (isMatch) {
      matches.push(searchString);
    }
  }

  return matches;
}

// Function to find fuzzy matches by removing characters from both ends
function findFuzzyMatches(text: string, searchStrings: string[]): string[] {
  const matches: string[] = [];

  for (const searchString of searchStrings) {
    // Normalize spaces in both search string and text
    const normalizedSearch = searchString.replace(/\s+/g, ' ').trim();
    const normalizedText = text.replace(/\s+/g, ' ').trim();

    const searchLower = normalizedSearch.toLowerCase();
    const textLower = normalizedText.toLowerCase();

    // Try exact match first
    if (textLower.includes(searchLower)) {
      matches.push(searchString);
      continue;
    }

    // Try fuzzy matching by removing characters from both ends
    let bestMatch = '';
    let bestMatchLength = 0;

    // Try removing from start
    for (let startRemove = 1; startRemove < searchLower.length / 2; startRemove++) {
      const truncatedStart = searchLower.substring(startRemove);
      if (textLower.includes(truncatedStart) && truncatedStart.length > bestMatchLength) {
        bestMatch = truncatedStart;
        bestMatchLength = truncatedStart.length;
      }
    }

    // Try removing from end
    for (let endRemove = 1; endRemove < searchLower.length / 2; endRemove++) {
      const truncatedEnd = searchLower.substring(0, searchLower.length - endRemove);
      if (textLower.includes(truncatedEnd) && truncatedEnd.length > bestMatchLength) {
        bestMatch = truncatedEnd;
        bestMatchLength = truncatedEnd.length;
      }
    }

    // Try removing from both ends
    for (let startRemove = 1; startRemove < searchLower.length; startRemove++) {
      for (let endRemove = 1; endRemove < searchLower.length - startRemove; endRemove++) {
        const truncatedBoth = searchLower.substring(startRemove, searchLower.length - endRemove);
        if (textLower.includes(truncatedBoth) && truncatedBoth.length > bestMatchLength) {
          bestMatch = truncatedBoth;
          bestMatchLength = truncatedBoth.length;
        }
      }
    }

    // Check if the best match is at least 50% of the original length
    if (bestMatch && bestMatchLength / searchLower.length >= 0.75) {
      matches.push(bestMatch);
    }
  }

  return matches;
}

// Function to get individual words for highlighting
function getIndividualWords(searchStrings: string[]): string[] {
  const words = new Set<string>();

  for (const searchString of searchStrings) {
    const stringWords = searchString
      .split(/\s+/)
      .map(word => word.replace(/\W/g, ''))
      .filter(word => word.length >= 4); // Only keep words with 4 or more characters

    for (const word of stringWords) words.add(word.toLowerCase());
  }

  return Array.from(words);
}

// Function to determine what to highlight
async function determineHighlightText(): Promise<string[]> {
  if (currentHighlightText.value.length === 0) {
    return [];
  }

  const pageTextValue = await extractPageText();
  pageText.value = pageTextValue;

  // Step 1: Find exact matches
  const exactMatches = findExactMatches(pageTextValue, currentHighlightText.value);

  // Step 2: For passages that weren't found exactly, try fuzzy matching
  const unfoundPassages = currentHighlightText.value.filter(
    passage => !exactMatches.includes(passage)
  );

  const fuzzyMatches = findFuzzyMatches(pageTextValue, unfoundPassages);

  // Step 3: For passages that still weren't found, extract individual words
  const stillUnfoundPassages = unfoundPassages.filter(
    passage =>
      !fuzzyMatches.includes(passage) &&
      !fuzzyMatches.some(fuzzy => passage.toLowerCase().includes(fuzzy.toLowerCase()))
  );

  let individualWords: string[] = [];
  if (stillUnfoundPassages.length > 0) {
    individualWords = getIndividualWords(stillUnfoundPassages);
  }

  // Step 4: Combine all matches
  const combinedHighlights = [...exactMatches, ...fuzzyMatches, ...individualWords];

  return combinedHighlights;
}

// Computed property for highlight text to pass to VuePDF
const highlightTextForPDF = ref<string[]>([]);

// Function to set highlight text externally
async function setHighlightText(texts: string[]): Promise<void> {
  currentHighlightText.value = texts;

  // Determine what to highlight
  const highlightPassages = await determineHighlightText();
  highlightTextForPDF.value = highlightPassages;
}

// Direct highlight: bypasses fuzzy/word logic — texts are passed straight to VuePDF
function setHighlightTextDirect(texts: string[]): void {
  highlightMode.value = 'direct';
  currentHighlightText.value = texts.filter(t => t.trim().length > 0);
  highlightTextForPDF.value = currentHighlightText.value;
}

// Function to clear highlights
function clearHighlights(): void {
  highlightMode.value = 'smart';
  currentHighlightText.value = [];
  highlightTextForPDF.value = [];
  emit('highlightsCleared');
}

// Expose methods for external use
defineExpose({
  goToPage,
  goToNextPage,
  goToPreviousPage,
  goToFirstPage,
  goToLastPage,
  reloadPDF,
  setHighlightText,
  setHighlightTextDirect,
  clearHighlights,
});
</script>

<template>
  <!-- Document preview pane, shared by the review workspace, the file vault, CM
       detail, playbook runs and the signing request detail. The chrome is the
       design system's; the exposed imperative API (goToPage, setHighlightText,
       clearHighlights, …) is a contract for all of those callers and does not
       change with it. -->
  <n-flex
    vertical
    :size="0"
    :wrap="false"
    class="vault-pdf-preview"
    :class="highlightColor ? `highlight-${highlightColor}` : ''"
  >
    <gaia-doc-pager
      v-if="loaded && totalPages > 0"
      :page="currentPage"
      :total="totalPages"
      @prev="goToPreviousPage"
      @next="goToNextPage"
    >
      <template #actions>
        <n-flex v-if="showZoom" inline align="center" :size="2" :wrap="false">
          <gaia-icon-button
            :dimension="28"
            quaternary
            aria-label="Zoom out"
            :disabled="zoom <= ZOOM_MIN"
            @click="setZoom(zoom - ZOOM_STEP)"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.9"
              stroke-linecap="round"
            >
              <path d="M6 12h12" />
            </svg>
          </gaia-icon-button>
          <gaia-button
            size="small"
            quaternary
            title="Reset zoom"
            :style="{ minWidth: '56px' }"
            @click="setZoom(props.defaultZoom)"
          >
            {{ Math.round(zoom * 100) }}%
          </gaia-button>
          <gaia-icon-button
            :dimension="28"
            quaternary
            aria-label="Zoom in"
            :disabled="zoom >= ZOOM_MAX"
            @click="setZoom(zoom + ZOOM_STEP)"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.9"
              stroke-linecap="round"
            >
              <path d="M6 12h12 M12 6v12" />
            </svg>
          </gaia-icon-button>
        </n-flex>
        <gaia-input
          v-model:value="jumpToPage"
          size="small"
          placeholder="Page"
          :style="{ width: '72px' }"
          @keyup.enter="handleJumpToPage"
        />
        <gaia-button size="small" :disabled="!isValidPageNumber" @click="handleJumpToPage">
          Go
        </gaia-button>
        <gaia-button
          v-if="hasHighlights"
          size="small"
          title="Clear highlights"
          @click="clearHighlights"
        >
          Clear highlights
        </gaia-button>
        <gaia-button
          v-if="downloadUrl"
          size="small"
          :loading="downloadingFile"
          :title="fileName"
          @click="downloadFile"
        >
          {{ downloadingFile ? 'Downloading…' : 'Download' }}
        </gaia-button>
      </template>
    </gaia-doc-pager>

    <div ref="pdfContainerRef" class="pdf-container">
      <!-- Waiting on the file's own metadata, before we know if it can preview. -->
      <n-flex
        v-if="isCheckingPreview && !vaultFileData"
        vertical
        align="center"
        justify="center"
        :size="10"
        :style="{ height: '100%' }"
      >
        <gaia-spinner :size="20" />
        <gaia-text size="meta" tone="subtle">Loading file information…</gaia-text>
      </n-flex>

      <!-- The backend renders the preview asynchronously after upload. -->
      <gaia-empty-state v-else-if="isPreviewInProgress" title="Generating preview">
        <template #icon><gaia-spinner :size="20" /></template>
        Creating a PDF preview for “{{ fileName }}”. This may take a few moments.
      </gaia-empty-state>

      <gaia-empty-state v-else-if="isPreviewError" status="alert" title="Preview generation failed">
        We could not generate a PDF preview for “{{ fileName }}”.
        <template #actions>
          <gaia-button size="small" @click="fetchVaultFileData">Try again</gaia-button>
        </template>
      </gaia-empty-state>

      <n-flex
        v-else-if="isInitializing || (isPreviewReady && !loaded)"
        vertical
        align="center"
        justify="center"
        :size="10"
        :style="{ height: '100%' }"
      >
        <gaia-spinner :size="20" />
        <gaia-text size="meta" tone="subtle">Loading document…</gaia-text>
      </n-flex>

      <gaia-empty-state v-else-if="error" status="alert" title="Could not open this document">
        {{ error }}
      </gaia-empty-state>

      <!-- Continuous: the whole document as one column. Every page keeps a
           wrapper of its true height so the scrollbar describes the document,
           but only the pages near the viewport carry a canvas. -->
      <div v-else-if="loaded && pdfInfo && isContinuous" class="pdf-viewer continuous">
        <div
          v-for="page in totalPages"
          :key="page"
          :ref="el => observePage(page, el as HTMLElement | null)"
          class="pdf-page"
          :style="[pageBoxStyle(page), zoom === 1 ? undefined : { width: `${zoom * 100}%` }]"
        >
          <vue-p-d-f
            v-if="isRendered(page)"
            :key="`pdf-${page}-${renderNonce}-${currentHighlightText.length > 0 ? 'highlight' : 'plain'}`"
            :pdf="pdfInfo.pdf"
            :page="page"
            text-layer
            hide-forms
            :highlight-text="page === currentPage ? highlightTextForPDF : []"
            :highlight-options="highlightOptions"
            fit-parent
          />
          <!-- Something to lay over the page box (the signing wizard's field
               canvas). Mounted for every page, rasterised or not, so what sits
               on a page exists before the page is drawn. -->
          <slot name="page-overlay" :page="page" :width-pts="pageWidthsPts.get(page) ?? null" />
        </div>
      </div>

      <!-- Paginated: one page, remounted on every page turn. -->
      <div v-else-if="loaded && pdfInfo" class="pdf-viewer">
        <vue-p-d-f
          ref="vuePDFRef"
          :key="`pdf-${currentPage}-${currentHighlightText ? 'highlight' : 'no-highlight'}`"
          :pdf="pdfInfo.pdf"
          :page="currentPage"
          text-layer
          hide-forms
          :highlight-text="highlightTextForPDF"
          :highlight-options="highlightOptions"
          fit-parent
        />
      </div>

      <gaia-empty-state v-else title="No document available" />
    </div>
  </n-flex>
</template>

<style scoped>
.vault-pdf-preview {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.pdf-container {
  flex: 1;
  overflow: auto;
  height: 0; /* Force content to respect the flex container */
  position: relative; /* Ensure proper positioning of the PDF viewer */
}

.pdf-viewer {
  height: 100%;
  overflow: auto;
  border: 1px solid v-bind(viewerBorder);
  border-radius: 4px;
  width: 100%;
}

/* Continuous mode: the pages stack and the container scrolls, so the viewer
   itself must not also scroll or the two fight over the wheel. */
.pdf-viewer.continuous {
  height: auto;
  overflow: visible;
  border: none;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  padding: 12px;
  background: v-bind(viewerCanvas);
}

/* Holds a page's height whether or not it currently carries a canvas, so
   scrolling past unrendered pages does not shift what is on screen.

   Block, not flex: the deep rules below size the canvas with `width: 100%`,
   which fills a block box but leaves a flex item free to shrink to its
   intrinsic size — which is what rendered the page at a third of the pane with
   the rest as whitespace. Paginated mode never hit it because its page sits
   directly in a block container. */
.pdf-page {
  position: relative; /* positioning context for the page-overlay slot */
  display: block;
  width: 100%;
  min-width: 0;
  /* Centres a zoomed-out page; a zoomed-in one resolves the margins to 0 and
     overflows to the right only, where the container can still scroll to it. */
  margin-inline: auto;
  background: v-bind(pageSurface);
  box-shadow: v-bind(pageLift);
}

.pdf-loading.error {
  color: #f44336;
}

.preview-icon.error {
  color: #f44336;
}

/* Deep styling for the PDF viewer */
:deep(.vue-pdf) {
  width: 100%;
  height: auto;
}

:deep(.vue-pdf canvas) {
  width: 100% !important;
  height: auto !important;
  max-width: 100%;
  object-fit: contain;
  display: block;
}

:deep(.vue-pdf .page) {
  width: 100% !important;
  height: auto !important;
  max-width: 100%;
  margin: 0 auto;
  display: block;
}

:deep(.vue-pdf .page canvas) {
  width: 100% !important;
  height: auto !important;
  max-width: 100%;
  object-fit: contain;
}

:deep(.vue-pdf .page .textLayer) {
  width: 100% !important;
  height: auto !important;
  max-width: 100%;
}

/* Version-compare marks. The red/green names are the caller's vocabulary
   (`highlight-color="red|green"`); the values are the diff palette's stronger
   alphas, since these sit over the document's own ink rather than over white. */
.highlight-red :deep(.highlight) {
  --highlight-bg-color: v-bind('gaiaDiff.removedHighlight');
}

.highlight-green :deep(.highlight) {
  --highlight-bg-color: v-bind('gaiaDiff.addedHighlight');
}
</style>
